import { MentionPopover } from '../../components/chat/MentionPopover';
import { useAdminAvatar } from '../../lib/avatars';
import { useMentionInput } from '../../components/chat/useMentionInput';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../components/ui';
import { getThemeColors, BRAND } from '../../lib/theme';
import { formatDayMonth, formatTime } from '../../lib/dateFormat';
import { adminChatApi, type ChatAdmin, type ChatMessage } from '../../lib/adminChat';
import { useAuth } from '../../contexts/AuthContext';
import type { Density } from '../../types';

interface Props {
  dark: boolean;
  density?: Density;
}

const POLL_INTERVAL = 5000;
const HEARTBEAT_INTERVAL = 30000;

function initials(name: string): string {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/** Tages-Trenner: Wochentag in UI-Sprache, Datum immer deutsch („Mo., 26.10."). */
function formatDateSeparator(iso: string, localeTag: string): string {
  const weekday = new Date(iso).toLocaleDateString(localeTag, { weekday: 'short' });
  return `${weekday}, ${formatDayMonth(iso)}`;
}

function isSameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

const AVATAR_COLORS = [
  '#7a5af8', '#2e90fa', '#12b76a', '#f79009',
  '#ee46bc', '#f04438', '#0ba5ec', '#15b79e',
];

function avatarColor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function Avatar({ admin, size = 34 }: { admin: ChatAdmin; size?: number }) {
  const bg = avatarColor(admin.id);
  // Feedback 11.10.2026: Profilbild des Mitarbeiters, sonst Initialen.
  const url = useAdminAvatar(admin.id);
  const [failed, setFailed] = useState(false);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700,
      flexShrink: 0, userSelect: 'none', overflow: 'hidden',
    }}>
      {url && !failed
        ? <img src={url} alt={admin.initials} onError={() => setFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : admin.initials}
    </div>
  );
}

export function ChatPage({ dark }: Props) {
  const { t, i18n } = useTranslation();
  const tc = getThemeColors(dark);
  // Zeit-/Datumsformate folgen der UI-Sprache statt fest 'de-DE'.
  const dateLocaleTag = i18n.language === 'pl' ? 'pl-PL' : i18n.language === 'en' ? 'en-US' : 'de-DE';
  const { user } = useAuth();

  const [messages, setMessages]     = useState<ChatMessage[]>([]);
  const [online, setOnline]         = useState<ChatAdmin[]>([]);
  const [input, setInput]           = useState('');
  const [sending, setSending]       = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [error, setError]           = useState<string | null>(null);

  const bottomRef  = useRef<HTMLDivElement>(null);
  const inputRef   = useRef<HTMLTextAreaElement>(null);
  const isFirstLoad = useRef(true);

  const scrollToBottom = (smooth = false) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  };

  const loadMessages = useCallback(async () => {
    try {
      const data = await adminChatApi.messages();
      setMessages(prev => {
        const changed = JSON.stringify(prev.map(m => m.id)) !== JSON.stringify(data.map(m => m.id));
        if (changed && !isFirstLoad.current) {
          setTimeout(() => scrollToBottom(true), 50);
        }
        return data;
      });
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        setTimeout(() => scrollToBottom(false), 50);
      }
    } catch {
      // silent poll failure
    }
  }, []);

  const loadPresence = useCallback(async () => {
    try {
      const data = await adminChatApi.presence();
      setOnline(data);
    } catch {
      // silent
    }
  }, []);

  const sendHeartbeat = useCallback(async () => {
    try {
      await adminChatApi.heartbeat();
      await loadPresence();
    } catch {
      // silent
    }
  }, [loadPresence]);

  useEffect(() => {
    void loadMessages();
    void sendHeartbeat();

    const msgTimer = window.setInterval(() => void loadMessages(), POLL_INTERVAL);
    const hbTimer  = window.setInterval(() => void sendHeartbeat(), HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(msgTimer);
      clearInterval(hbTimer);
    };
  }, [loadMessages, sendHeartbeat]);

  // @-Erwähnungen: die Auswahlliste schreibt in dasselbe input-State.
  const mentions = useMentionInput({ value: input, onChange: setInput, inputRef });

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      const msg = await adminChatApi.send(text);
      setInput('');
      mentions.reset();
      setMessages(prev => [...prev, msg]);
      setTimeout(() => scrollToBottom(true), 50);
      inputRef.current?.focus();
    } catch {
      setError(t('chat.error.send_failed'));
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await adminChatApi.remove(id);
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch {
      // silent
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter wählt erst die Erwähnung aus, bevor es die Nachricht sendet.
    if (mentions.handleKeyDown(e)) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const currentAdminId = (user as { id?: number } | null)?.id;

  // ── Styles ─────────────────────────────────────────────────────────────────

  const PAGE: React.CSSProperties = {
    display: 'flex', height: '100%', fontFamily: 'Outfit, sans-serif',
    background: tc.bg, overflow: 'hidden',
  };

  const SIDEBAR: React.CSSProperties = {
    width: 240, flexShrink: 0,
    borderRight: `1px solid ${tc.border}`,
    display: 'flex', flexDirection: 'column',
    background: tc.card,
  };

  const CHAT_AREA: React.CSSProperties = {
    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
  };

  const MESSAGES: React.CSSProperties = {
    flex: 1, overflowY: 'auto',
    padding: '20px 24px',
    display: 'flex', flexDirection: 'column', gap: 2,
  };

  const INPUT_ROW: React.CSSProperties = {
    borderTop: `1px solid ${tc.border}`,
    padding: '14px 20px',
    display: 'flex', gap: 10, alignItems: 'flex-end',
    background: tc.card, flexShrink: 0,
  };

  const TEXTAREA: React.CSSProperties = {
    flex: 1, resize: 'none',
    border: `1px solid ${tc.border}`,
    borderRadius: 10, padding: '10px 14px',
    fontSize: 14, color: tc.text,
    background: tc.inputBg, fontFamily: 'Outfit, sans-serif',
    outline: 'none', lineHeight: 1.5, maxHeight: 120,
  };

  return (
    <div style={PAGE}>
      {/* ── Online sidebar ── */}
      <aside style={SIDEBAR}>
        <div style={{ padding: '20px 16px 12px', flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: tc.placeholder, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
            {t('chat.online', { count: online.length })}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 16px' }}>
          {online.length === 0 && (
            <div style={{ fontSize: 12, color: tc.muted, padding: '0 4px' }}>
              {t('chat.no_one_online')}
            </div>
          )}
          {online.map(admin => (
            <div key={admin.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '7px 8px', borderRadius: 8,
              background: admin.id === currentAdminId ? (dark ? 'rgba(122,90,248,0.12)' : 'rgba(122,90,248,0.07)') : 'transparent',
            }}>
              <div style={{ position: 'relative' }}>
                <Avatar admin={admin} size={30} />
                <span style={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: 9, height: 9, borderRadius: '50%',
                  background: '#12b76a',
                  border: `2px solid ${tc.card}`,
                }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: tc.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {admin.name}{admin.id === currentAdminId ? ` (${t('chat.label.you')})` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* ── Chat area ── */}
      <div style={CHAT_AREA}>
        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: `1px solid ${tc.border}`,
          background: tc.card, flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%', background: '#12b76a', flexShrink: 0,
          }} />
          <div style={{ fontWeight: 700, fontSize: 15, color: tc.heading }}>
            {t('chat.title')}
          </div>
          <div style={{ fontSize: 12, color: tc.muted }}>
            {t('chat.header_subtitle', { count: online.length })}
          </div>
        </div>

        {/* Messages */}
        <div style={MESSAGES}>
          {messages.map((msg, i) => {
            const isOwn = msg.admin.id === currentAdminId;
            const prevMsg = messages[i - 1];
            const showDateSep = !prevMsg || !isSameDay(prevMsg.created_at, msg.created_at);
            const sameAuthor = prevMsg && prevMsg.admin.id === msg.admin.id && !showDateSep;

            return (
              <React.Fragment key={msg.id}>
                {showDateSep && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    margin: '16px 0 8px',
                  }}>
                    <div style={{ flex: 1, height: 1, background: tc.borderSoft }} />
                    <span style={{ fontSize: 11, color: tc.muted, whiteSpace: 'nowrap' }}>
                      {formatDateSeparator(msg.created_at, dateLocaleTag)}
                    </span>
                    <div style={{ flex: 1, height: 1, background: tc.borderSoft }} />
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  flexDirection: isOwn ? 'row-reverse' : 'row',
                  gap: 8,
                  alignItems: 'flex-end',
                  marginTop: sameAuthor ? 2 : 10,
                }}>
                  {/* Avatar spacer / avatar */}
                  <div style={{ width: 32, flexShrink: 0 }}>
                    {!sameAuthor && !isOwn && <Avatar admin={msg.admin} size={32} />}
                  </div>

                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: isOwn ? 'flex-end' : 'flex-start',
                    maxWidth: '70%',
                  }}>
                    {!sameAuthor && (
                      <div style={{
                        fontSize: 11, fontWeight: 600, color: tc.muted,
                        marginBottom: 3,
                        paddingLeft: isOwn ? 0 : 2,
                        paddingRight: isOwn ? 2 : 0,
                      }}>
                        {isOwn ? t('chat.label.you') : msg.admin.name}
                      </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, flexDirection: isOwn ? 'row-reverse' : 'row' }}>
                      <div
                        className="chat-bubble"
                        style={{
                          padding: '9px 14px',
                          borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          background: isOwn ? BRAND : (dark ? tc.cardMuted : '#f0f0f5'),
                          color: isOwn ? '#fff' : tc.text,
                          fontSize: 14, lineHeight: 1.5,
                          wordBreak: 'break-word',
                          position: 'relative',
                        }}
                        onMouseEnter={isOwn ? (e) => {
                          const btn = (e.currentTarget as HTMLElement).querySelector('.delete-btn') as HTMLElement | null;
                          if (btn) btn.style.opacity = '1';
                        } : undefined}
                        onMouseLeave={isOwn ? (e) => {
                          const btn = (e.currentTarget as HTMLElement).querySelector('.delete-btn') as HTMLElement | null;
                          if (btn) btn.style.opacity = '0';
                        } : undefined}
                      >
                        {msg.message}
                        {isOwn && (
                          <button
                            className="delete-btn"
                            onClick={() => void handleDelete(msg.id)}
                            disabled={deletingId === msg.id}
                            style={{
                              position: 'absolute', top: -8, right: -8,
                              width: 20, height: 20, borderRadius: '50%',
                              background: '#b42318', color: '#fff',
                              border: 'none', cursor: 'pointer',
                              fontSize: 11, lineHeight: 1,
                              opacity: 0, transition: 'opacity 0.15s',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <span style={{ fontSize: 10, color: tc.muted, flexShrink: 0, marginBottom: 2 }}>
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: tc.muted, fontSize: 13, marginTop: 60 }}>
              {t('chat.no_messages')}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ ...INPUT_ROW, position: 'relative' }}>
          <MentionPopover dark={dark} {...mentions.popoverProps} />
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={mentions.handleInput}
            onSelect={mentions.handleSelect}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.input_placeholder')}
            style={TEXTAREA}
          />
          <Button
            onClick={() => void handleSend()}
            disabled={sending || !input.trim()}
            dark={dark}
          >
            {t('chat.send')}
          </Button>
        </div>

        {error && (
          <div style={{ padding: '6px 20px 10px', fontSize: 12, color: '#b42318' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
